/**
 * Silences the benign "ResizeObserver loop ..." error.
 *
 * `@formily/grid` (used by every FormGrid, and our permit forms are full of them)
 * bundles the `@juggle/resize-observer` ponyfill. When a resize callback triggers
 * another layout in the same pass it emulates the native browser warning by doing
 * `window.dispatchEvent(new ErrorEvent('error', { message: 'ResizeObserver loop
 * completed with undelivered notifications.' }))`. It is harmless, but the CRA dev
 * error overlay surfaces it full-screen.
 *
 * A plain `addEventListener('error', …)` can't stop it: for an event dispatched
 * directly on `window`, listeners fire in registration order (capture is ignored
 * at the target), and CRA's overlay registers its listener before our app code
 * runs. So we intercept `window.dispatchEvent` itself and drop just this one
 * event before any listener sees it. Everything else passes through untouched.
 */
const MESSAGE =
  /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;

if (typeof window !== "undefined") {
  const isBenign = (e: unknown): boolean =>
    !!e &&
    typeof (e as ErrorEvent).message === "string" &&
    MESSAGE.test((e as ErrorEvent).message);

  // Primary fix: swallow the synthetic ErrorEvent at the dispatch source.
  const originalDispatch = window.dispatchEvent.bind(window);
  window.dispatchEvent = (event: Event): boolean => {
    if (event && event.type === "error" && isBenign(event)) return true;
    return originalDispatch(event);
  };

  // Belt-and-suspenders for any path that reports it differently.
  window.addEventListener(
    "error",
    (event) => {
      if (isBenign(event)) {
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    },
    true
  );

  const originalConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    if (typeof args[0] === "string" && MESSAGE.test(args[0])) return;
    originalConsoleError(...args);
  };
}

export {};
